/**
 * 네트워크 상태 감지 Hook
 * Capacitor Network 플러그인을 사용하여 온/오프라인 상태 추적
 */

import { useState, useEffect, useCallback } from "react";
import { Network, ConnectionStatus } from "@capacitor/network";

export interface NetworkState {
  /** 온라인 여부 */
  isOnline: boolean;
  /** 오프라인 여부 */
  isOffline: boolean;
  /** 연결 타입 (wifi, cellular, none, unknown) */
  connectionType: string;
  /** 상태 로딩 중 */
  isLoading: boolean;
}

/**
 * 네트워크 상태를 감지하고 반환하는 Hook
 */
export function useNetworkStatus(): NetworkState {
  // 초기 상태: 네트워크 상태 확인 전까지 오프라인으로 가정 (캐시 우선)
  const [state, setState] = useState<NetworkState>({
    isOnline: false,
    isOffline: true,
    connectionType: "unknown",
    isLoading: true,
  });

  const updateStatus = useCallback((status: ConnectionStatus) => {
    setState({
      isOnline: status.connected,
      isOffline: !status.connected,
      connectionType: status.connectionType,
      isLoading: false,
    });
  }, []);

  useEffect(() => {
    // 초기 상태 확인
    Network.getStatus()
      .then(updateStatus)
      .catch(() => {
        // 브라우저 환경에서는 navigator.onLine 사용
        setState({
          isOnline: navigator.onLine,
          isOffline: !navigator.onLine,
          connectionType: navigator.onLine ? "unknown" : "none",
          isLoading: false,
        });
      });

    // 상태 변경 리스너 등록
    const listener = Network.addListener("networkStatusChange", updateStatus);

    // 브라우저 폴백 이벤트
    const handleOnline = () => {
      setState((prev) => ({
        ...prev,
        isOnline: true,
        isOffline: false,
      }));
    };

    const handleOffline = () => {
      setState((prev) => ({
        ...prev,
        isOnline: false,
        isOffline: true,
      }));
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      listener.then((l) => l.remove());
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [updateStatus]);

  return state;
}

export default useNetworkStatus;
